import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  FinalCta,
  HEADLINE_FROM,
  HEADLINE_TO,
  HeroLights,
  makeReveal,
  playWhenSeen,
  useSectionReveal,
} from '@/components/marketing';
import { Glow, Icon, LinkButton } from '@/components/ui';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

/**
 * The journey diagram's coordinate space: the measure's 1410px across, and
 * the height the frame gives the curve. Everything below -- the path, the
 * dots, the numerals and the copy -- is placed in these units, so they scale
 * together. Read off the Figma frame with its content box at (255, 1214).
 */
const VIEWBOX = { width: 1410, height: 1220 };

/**
 * The rope's turning points, top to tail. Every one is an extreme of the S
 * -- the leftmost or rightmost point of a bend -- where the line runs
 * vertically, and one cubic joins each pair with vertical tangents at both
 * ends. That is what keeps the frame's curves smooth: no bend is made of
 * more than one piece. The three nodes are the turning points the steps
 * hang off. Traced from the Figma frame.
 */
const TURNS = [
  [966, 86],
  [761, 212], // node 1
  [835, 380],
  [445, 634], // node 2
  [1057, 1004], // node 3
];

/** Where the rope starts and ends, and the control points that aim it. */
const START = { at: [849, 5], via: [905, 5] };
const END = { at: [978, 1200], via: [990, 1150] };

/** How far each cubic's handles reach along the vertical: 0.5 is a clean S. */
const REACH = 0.55;

function ropePath() {
  const [first, ...rest] = TURNS;
  let d = `M ${START.at} C ${START.via}, ${first[0]} ${
    first[1] - REACH * (first[1] - START.at[1])
  }, ${first}`;
  let from = first;
  for (const to of rest) {
    const reach = REACH * (to[1] - from[1]);
    d += ` C ${from[0]} ${from[1] + reach}, ${to[0]} ${to[1] - reach}, ${to}`;
    from = to;
  }
  return `${d} C ${from[0]} ${from[1] + REACH * (END.at[1] - from[1])}, ${END.via}, ${END.at}`;
}

const PATH = ropePath();
const NODES = [TURNS[1], TURNS[3], TURNS[4]];

/**
 * The lights that ride the whole line, top to tail: three dots in a cluster,
 * the lead one largest, the others a fixed distance behind it along the
 * line. Off the Figma frame: 6.2, 5.2 and 4px across, 11 and 19px back.
 * Radii and distances in viewBox units.
 */
const FLOW = [
  { r: 3.1, behind: 0 },
  { r: 2.6, behind: 11 },
  { r: 2, behind: 19 },
];

/** Seconds a cluster takes to travel the whole route. */
const FLOW_SPEED = 16;

/** How many clusters travel together, and the distance between them. */
const FLOW_CLUSTERS = 2;
const FLOW_GAP = 150;

/**
 * Where each step's copy and ghost numeral sit in the viewBox. `at` is the
 * copy's anchor -- its top-left, or top-right for the step that sits to the
 * left of its node -- and `numeral` the digit's top-left. The
 * numerals are set so the digit's ink is centred on its node's height and
 * tucked inside the bend, about 110px in from the node, as the frame has
 * them; the digit box carries ~22px of dead space above the ink at this
 * size, which is folded in here.
 */
const STEPS = [
  {
    title: 'Create your company profile',
    body: 'Showcase your startup, culture, and what you’re looking for.',
    at: [848, 185],
    numeral: [647, 134],
  },
  {
    title: 'Post your opportunity',
    body: 'Tell us what you need, and Wonder surfaces the students who best match your role.',
    at: [392, 589],
    numeral: [513, 556],
    align: 'right',
  },
  {
    title: 'Meet your matches',
    body: 'See the students ranked best without screening hundreds of CVs.',
    at: [1091, 982],
    numeral: [898, 926],
  },
];

const REASONS = [
  {
    title: 'Skip screening',
    body: 'We shortlist. You interview. Save weeks on every hire.',
  },
  {
    title: 'Access the best talent',
    body: 'Reach ambitious students from 80+ schools across Europe.',
  },
  {
    title: 'AI-ranked shortlists',
    body: 'Every candidate scored by real fit, not keyword bingo.',
  },
];

/**
 * The ribbons inside each sphere: a long soft ellipse each, set at a tilt,
 * orbiting the centre over `orbit` seconds and flexing over `flex`. Started
 * at different phases and one running the other way, so the three never
 * line up and the churn never visibly repeats. Sizes as a share of the
 * sphere.
 */
const RIBBONS = [
  {
    width: 150,
    height: 34,
    tilt: -30,
    orbit: 18,
    flex: 7,
    phase: 0,
    color:
      'linear-gradient(90deg, rgba(240,110,220,0) 0%, rgba(240,110,220,0.22) 35%, rgba(196,176,250,0.22) 65%, rgba(196,176,250,0) 100%)',
  },
  {
    width: 140,
    height: 26,
    tilt: 40,
    orbit: 26,
    flex: 9,
    phase: 9,
    reverse: true,
    color:
      'linear-gradient(90deg, rgba(196,176,250,0) 0%, rgba(230,215,255,0.18) 40%, rgba(255,255,255,0.12) 60%, rgba(196,176,250,0) 100%)',
  },
  {
    width: 160,
    height: 30,
    tilt: 85,
    orbit: 32,
    flex: 11,
    phase: 17,
    color:
      'linear-gradient(90deg, rgba(122,85,232,0) 0%, rgba(122,85,232,0.3) 35%, rgba(240,110,220,0.18) 70%, rgba(240,110,220,0) 100%)',
  },
];

/**
 * Keeps only the padding of an element -- a ring, on a circle. Two mask
 * layers, one clipped to the content box, composited so they cancel where
 * they overlap. The `mask` shorthand, not a utility: the content-box clip
 * on the first layer is what makes it a ring, and `mask-image` alone cannot
 * say it.
 */
const RING = {
  mask: 'linear-gradient(#000, #000) content-box, linear-gradient(#000, #000)',
  maskComposite: 'exclude',
  WebkitMaskComposite: 'xor',
};

const PLANS = [
  {
    name: 'Pay-as-you-go',
    price: '€79',
    unit: 'Offer',
    tagline: 'No commitment. Post when you’re hiring.',
    features: ['1 internship post', 'Valid for 6 months', 'AI matching included'],
  },
  {
    name: 'Essential',
    price: '€199',
    unit: '/pack of 3',
    tagline: 'For startups hiring few interns',
    features: [
      '3 internship listings',
      'Valid for 12 months',
      'AI matching included',
      'Advanced analytics',
    ],
    featured: true,
  },
  {
    name: 'Growth',
    price: '€349',
    unit: '/pack of 6',
    tagline: 'For startups scaling up their hiring',
    features: [
      '6 internship offers',
      'Valid for 12 months',
      'AI matching included',
      'Advanced analytics',
    ],
    featured: true,
  },
  {
    name: 'Startup Partner',
    price: '€799',
    unit: '/year',
    tagline: 'For startups hiring regularly',
    features: [
      'Up to 10 listings',
      'AI matching included',
      'Enhanced startup profile',
      '2 boosts included',
    ],
  },
];

export function CompaniesPage() {
  return (
    <>
      <Hero />
      <HowItWorks />
      <WhyWonder />
      <Pricing />
      <FinalCta>
        <LinkButton
          to={paths.registerCompany}
          variant="light"
          shape="pill"
          size="pillMd"
          className="js-cta-button"
        >
          Start Hiring
        </LinkButton>
      </FinalCta>
    </>
  );
}

/** Position within the viewBox as CSS percentages. */
function at([x, y], anchor = 'left') {
  return {
    [anchor]: `${((anchor === 'right' ? VIEWBOX.width - x : x) / VIEWBOX.width) * 100}%`,
    top: `${(y / VIEWBOX.height) * 100}%`,
  };
}

function Hero() {
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal('.js-hero-line', HEADLINE_FROM, { ...HEADLINE_TO, duration: 2.2 }, 0.1);
      reveal(
        '.js-hero-copy',
        { y: 24, opacity: 0 },
        { duration: 0.9, stagger: 0.12, ease: 'power3.out' },
        1.1,
      );
    },
    { start: 'top 80%', once: true },
  );

  return (
    <section
      ref={root}
      className="relative isolate flex min-h-dvh flex-col justify-center overflow-hidden pt-28 pb-16 text-center lg:pt-32"
    >
      <HeroLights />

      <div className="wonder-container relative">
        <h1 className="font-tight text-[clamp(1.75rem,3.125vw,3.75rem)] leading-[1.292] font-semibold tracking-[-0.0253em] uppercase lg:whitespace-nowrap">
          <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
            <span className="js-hero-line text-on-night block">Find top talent for your</span>
          </span>{' '}
          <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
            <span className="js-hero-line text-iris-300 block">Company&rsquo;s future.</span>
          </span>
        </h1>
        <p className="js-hero-copy mx-auto mt-6 max-w-208 text-lg leading-7 text-white/80">
          Skip weeks of screening. Wonder gives you pre-vetted university students, AI-ranked
          shortlists and simple pricing that scales with your team.
        </p>
        <div className="js-hero-copy mt-8 flex flex-wrap justify-center gap-4">
          <LinkButton to={paths.registerCompany} variant="light" shape="pill" size="pillMd">
            Post An Opportunity
            <Icon name="arrowRight" size={16} />
          </LinkButton>
          <LinkButton to="#pricing" variant="outlineLight" shape="pill" size="pillMd">
            See Pricing
            <Icon name="arrowRight" size={16} />
          </LinkButton>
        </div>
      </div>
    </section>
  );
}

/**
 * The three steps on a vertical S. Above lg the curve is an SVG in the box
 * and the copy is placed against its viewBox; below, the curve is dropped and
 * the steps stack.
 *
 * Each step arrives on its own, as the reader scrolls down to it: its
 * numeral drifts up, its node swells onto the line, and its title rises out
 * of a mask with the copy fading up under it. The section is taller than a
 * viewport, so one entrance for all three would play the last step out of
 * sight; each is triggered off its own copy instead.
 *
 * Each node breathes with the same ring the WONDER word uses, and clusters
 * of lights ride the line from its top to its tail on a loop. The lights are
 * the only thing GSAP drives per frame: a plain number from 0 to 1, read
 * back off the path. Only above lg, where the curve is drawn, and never
 * under reduced motion.
 */
function HowItWorks() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const media = gsap.matchMedia();

    media.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(root.current);
      const [path] = select('.js-route');
      const lights = select('.js-flow');
      const total = path.getTotalLength();

      // Which way the train runs. Reading down the page it flows from above
      // step one to past step three; scrolling back up it runs the other way,
      // three to one. The reverse is a sign on the head's travel rather than
      // a mirrored position, so the lights turn around from wherever they are
      // instead of jumping across the curve.
      let direction = 1;
      ScrollTrigger.create({
        trigger: root.current,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: (self) => {
          direction = self.direction;
        },
      });

      // The head of the train is one number walked along the line, and every
      // other dot is placed off it at its distance back -- its cluster's gap
      // plus its own -- so the gaps hold whichever way it moves. The run
      // overshoots the line by the train's length so the last dot leaves
      // before the head comes back in at the far end; anything off either
      // end of the line is hidden.
      const tail = (FLOW_CLUSTERS - 1) * FLOW_GAP + FLOW.at(-1).behind;
      const wrap = gsap.utils.wrap(0, total + tail);
      const pace = total / FLOW_SPEED;
      let head = 0;

      // A ticker rather than a tween: a repeating tween cannot be run
      // backwards past its own start, and turning the train around is the
      // whole point.
      const drive = (_, delta) => {
        head = wrap(head + (delta / 1000) * pace * direction);
        lights.forEach((dot, index) => {
          const cluster = Math.floor(index / FLOW.length);
          const light = FLOW[index % FLOW.length];
          const along = head - cluster * FLOW_GAP - light.behind;
          const onLine = along >= 0 && along <= total;
          dot.setAttribute('opacity', onLine ? 1 : 0);
          if (!onLine) return;
          const point = path.getPointAtLength(along);
          dot.setAttribute('cx', point.x);
          dot.setAttribute('cy', point.y);
        });
      };

      gsap.ticker.add(drive);
      return () => gsap.ticker.remove(drive);
    });

    media.add('(prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(root.current);

      select('.js-step').forEach((step, index) => {
        const entrance = gsap.timeline({ paused: true });
        const reveal = makeReveal(entrance, select);
        reveal(
          `.js-numeral-${index}`,
          { y: 40, opacity: 0 },
          { duration: 1.4, ease: 'power3.out' },
          0,
        );
        reveal(
          `.js-node-${index}`,
          { scale: 0, opacity: 0, transformOrigin: '50% 50%' },
          { duration: 0.9, ease: 'back.out(1.8)' },
          0.1,
        );
        reveal(
          step.querySelectorAll('.js-step-title'),
          HEADLINE_FROM,
          { ...HEADLINE_TO, duration: 2 },
          0.2,
        );
        reveal(
          step.querySelectorAll('.js-step-body'),
          { y: 16, opacity: 0 },
          { duration: 0.9, ease: 'power3.out' },
          0.6,
        );
        playWhenSeen(step, entrance);
      });
    });

    return () => media.revert();
  }, []);

  return (
    <section
      ref={root}
      id="how-it-works"
      className="bg-night relative overflow-hidden py-20 lg:py-28"
    >
      <Glow className="top-[20%] -left-40" size={640} opacity={0.12} />
      <Glow className="top-[50%] -right-32" size={520} tone="lilac" opacity={0.1} />

      <div className="wonder-container wonder-measure relative">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-tight text-on-night text-[clamp(2.25rem,3.125vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.0333em]">
            How Wonder <span className="text-iris-300">Works</span>
          </h2>
          <p className="text-on-night-muted mt-6 text-[clamp(1rem,1.04vw,1.25rem)] leading-relaxed">
            Wonder uses AI to understand what students want and what startups need.
          </p>
        </div>

        <div
          className="font-tight relative mx-auto mt-16 lg:mt-8"
          style={{ aspectRatio: `${VIEWBOX.width} / ${VIEWBOX.height}` }}
        >
          <svg
            aria-hidden="true"
            className="absolute inset-0 hidden h-full w-full overflow-visible lg:block"
            viewBox={`0 0 ${VIEWBOX.width} ${VIEWBOX.height}`}
            fill="none"
          >
            <defs>
              <filter id="co-bloom" x="-20%" y="-10%" width="140%" height="120%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
              <filter id="co-halo" x="-150%" y="-150%" width="400%" height="400%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
            </defs>

            {/* Inspector: a 2px #CEA2FF stroke under a soft glow of the same
                colour. Drawn twice -- wide and blurred, then thin and sharp
                -- so it reads as light rather than a line with a shadow. */}
            <path
              d={PATH}
              stroke="#CEA2FF"
              strokeWidth="12"
              strokeLinecap="round"
              opacity="0.45"
              filter="url(#co-bloom)"
            />
            <path
              className="js-route"
              d={PATH}
              stroke="#CEA2FF"
              strokeWidth="2"
              strokeLinecap="round"
            />

            {NODES.map(([x, y], index) => (
              <g key={`${x}-${y}`} className={`js-node-${index}`}>
                <circle cx={x} cy={y} r="18" fill="#C4B0FA" opacity="0.5" filter="url(#co-halo)" />
                {/* The ring is CSS, as on the WONDER word: it runs for the
                    life of the page and costs nothing per frame. Offset per
                    node so the three do not breathe in step. */}
                <circle
                  className="wonder-node-pulse"
                  style={{ animationDuration: '2.8s', animationDelay: `-${index * 0.9}s` }}
                  cx={x}
                  cy={y}
                  r="10"
                  stroke="#E8DDFF"
                  strokeWidth="2.5"
                />
                <circle cx={x} cy={y} r="7" fill="#FFFFFF" />
              </g>
            ))}

            {/* Hidden until the effect sets them moving. */}
            {Array.from({ length: FLOW_CLUSTERS }, (_, cluster) =>
              FLOW.map((light) => (
                <circle
                  key={`${cluster}-${light.r}`}
                  className="js-flow"
                  r={light.r}
                  fill="#FFFFFF"
                  opacity="0"
                />
              )),
            )}
          </svg>

          <ol className="space-y-24 lg:space-y-0">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                // Step two is anchored by its right edge, against its node,
                // but its text still reads from the left, as the frame sets
                // it. Widths off the inspector: the copy is 329.8px wide,
                // which breaks step one after "what"; step two's is wider so
                // "the" stays on its first line.
                className={cn(
                  'js-step relative lg:absolute',
                  step.align === 'right' ? 'lg:w-96' : 'lg:w-[329.8px]',
                )}
                style={at(step.at, step.align)}
              >
                {/* Below lg the numeral ghosts above its own copy; above,
                    the placed list further down takes over. */}
                <span
                  aria-hidden="true"
                  className={`js-numeral-${index} pointer-events-none absolute bottom-full left-0 -mb-5 font-sans text-[6rem] leading-none font-bold text-white/15 select-none lg:hidden`}
                >
                  {index + 1}
                </span>
                <div className="relative">
                  <h3 className="text-on-night overflow-hidden text-[1.375rem] leading-9 font-semibold">
                    <span className="js-step-title block">{step.title}</span>
                  </h3>
                  <p className="js-step-body mt-3 text-lg leading-6 text-white/70">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* The numerals are placed against the box, not their step: in the
              frame each sits beside its node rather than above its copy. */}
          <ol aria-hidden="true" className="pointer-events-none hidden lg:block">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className={`js-numeral-${index} absolute font-sans text-[9.75rem] leading-none font-bold text-white/15 select-none`}
                style={at(step.numeral)}
              >
                {index + 1}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/**
 * "Why you should choose Wonder": three glass spheres.
 *
 * The entrance, on the page's own terms:
 *
 *   0.00  the two heading lines rise out of their masks
 *   0.40  the subline comes up under them
 *   0.70  the spheres swell into place, left to right
 *   1.10  inside each, the title rises out of its mask and the copy fades up
 */
function WhyWonder() {
  const root = useSectionReveal(({ reveal }) => {
    reveal('.js-why-line', HEADLINE_FROM, HEADLINE_TO);
    reveal('.js-why-sub', { y: 24, opacity: 0 }, { duration: 0.8, ease: 'power3.out' }, 0.4);
    reveal(
      '.js-orb',
      { scale: 0.7, y: 40, opacity: 0 },
      { duration: 1, stagger: 0.15, ease: 'back.out(1.6)' },
      0.7,
    );
    reveal(
      '.js-orb-title',
      { yPercent: 110, opacity: 0.4, filter: 'blur(6px)' },
      { duration: 0.8, stagger: 0.15, ease: 'power3.out' },
      1.1,
    );
    reveal(
      '.js-orb-body',
      { y: 14, opacity: 0 },
      { duration: 0.7, stagger: 0.15, ease: 'power2.out' },
      1.3,
    );
  });

  // The rim's turn. Under the pointer it gathers speed into a steady six
  // seconds a lap; when the pointer leaves it does not snap home but turns
  // back the way it came, easing to rest where it started. GSAP rather than
  // a hover animation in CSS, which is dropped -- and the rim with it -- the
  // instant the pointer goes.
  useLayoutEffect(() => {
    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference) and (hover: hover)', () => {
      const rims = gsap.utils.selector(root.current)('.js-rim');
      const cleanups = rims.map((rim) => {
        const lap = { duration: 6, ease: 'none' };
        let turn;
        const enter = () => {
          turn?.kill();
          turn = gsap
            .timeline()
            .to(rim, { rotation: '+=15', duration: 0.5, ease: 'power2.in' })
            .to(rim, { rotation: '+=360', repeat: -1, ...lap });
        };
        // Back at the same rate as the lap: however far round it got, it
        // returns at 60 degrees a second.
        const leave = () => {
          turn?.kill();
          const angle = gsap.getProperty(rim, 'rotation') % 360;
          gsap.set(rim, { rotation: angle });
          turn = gsap.to(rim, { rotation: 0, duration: angle / 60, ease: 'none' });
        };
        const orb = rim.parentElement;
        orb.addEventListener('pointerenter', enter);
        orb.addEventListener('pointerleave', leave);
        return () => {
          orb.removeEventListener('pointerenter', enter);
          orb.removeEventListener('pointerleave', leave);
          turn?.kill();
        };
      });
      return () => cleanups.forEach((cleanup) => cleanup());
    });

    return () => media.revert();
  }, [root]);

  return (
    <section ref={root} className="bg-night relative overflow-hidden py-20 lg:py-28">
      <Glow className="top-1/3 -left-40" size={680} opacity={0.14} />
      <Glow className="-right-32 bottom-0" size={560} tone="lilac" opacity={0.1} />

      <div className="wonder-container wonder-measure relative">
        <h2 className="font-tight text-on-night mx-auto max-w-4xl text-center text-[clamp(2.25rem,3.125vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.0333em]">
          <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
            <span className="js-why-line block">Why you should choose</span>
          </span>{' '}
          <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
            <span className="js-why-line text-iris-300 block">Wonder</span>
          </span>
        </h2>
        <p className="js-why-sub mx-auto mt-8 max-w-208 text-center text-[clamp(1rem,1.05vw,1.25rem)] leading-normal text-white/80">
          Wonder compares skills, ambition, availability and startup needs to surface the best
          internship opportunities automatically.
        </p>

        <ul className="mt-16 flex flex-wrap items-center justify-center gap-10 lg:gap-14">
          {REASONS.map((reason) => (
            <li
              key={reason.title}
              // The glass, layer for layer off the Figma frame. The sphere is
              // not painted: it is a stack of 300px ellipses with 1% fills,
              // inner shadows and blurred rings over the page's own ground,
              // and the same stack is built here. Figma's rotation runs
              // counter-clockwise, so every angle below has its sign flipped.
              //
              // This element is Ellipse 2890: #C4C4C4 at 1%, an inner shadow
              // of #C09BFF at -4/-4 blurred 16, and a 20px background blur.
              className="js-orb group relative flex size-75 flex-col items-center justify-center rounded-full bg-[rgba(196,196,196,0.01)] p-8 text-center shadow-[inset_-4px_-4px_16px_#C09BFF] backdrop-blur-[20px]"
            >
              {/* Ellipse 2889: at 70%, an inner shadow of #5013FF at -24/-24
                  blurred 54 -- the blue-violet body of the sphere. */}
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full opacity-70 shadow-[inset_-24px_-24px_54px_#5013FF]"
              />

              {/* Inside the glass, clipped to it: the frame's two blooms,
                  and under them three soft ribbons of colour orbiting the
                  centre at their own rates, each slowly flexing as it goes,
                  with a core that breathes -- the way the reference sphere
                  churns. Transform-only, so each blur is rasterised once
                  and the motion runs on the compositor. */}
              <span
                aria-hidden="true"
                className="absolute inset-0 overflow-hidden rounded-full opacity-45 transition-opacity duration-700 group-hover:opacity-100"
              >
                {RIBBONS.map((ribbon, index) => (
                  <span
                    key={index}
                    className="absolute inset-0 motion-safe:animate-spin"
                    style={{
                      animationDuration: `${ribbon.orbit}s`,
                      animationDelay: `-${ribbon.phase}s`,
                      animationDirection: ribbon.reverse ? 'reverse' : 'normal',
                    }}
                  >
                    <span
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[50%] blur-[14px] motion-safe:animate-[wonder-ribbon_var(--flex)_ease-in-out_infinite_alternate]"
                      style={{
                        width: `${ribbon.width}%`,
                        height: `${ribbon.height}%`,
                        rotate: `${ribbon.tilt}deg`,
                        background: ribbon.color,
                        '--flex': `${ribbon.flex}s`,
                        animationDelay: `-${ribbon.phase / 2}s`,
                      }}
                    />
                  </span>
                ))}
                <span className="absolute top-1/2 left-1/2 size-[50%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.14)_0%,rgba(240,200,255,0.07)_35%,rgba(255,255,255,0)_65%)] blur-sm motion-safe:animate-[wonder-breathe_5s_ease-in-out_infinite_alternate]" />
                {/* Ellipse 2892: 41.2 x 68.94 at (60, 93.75), turned -45deg,
                    #FF9BC5 at 70%, blurred 24. */}
                <span className="absolute top-[31.25%] left-[20%] h-[22.98%] w-[13.73%] rotate-45 rounded-[50%] bg-[#FF9BC5] opacity-70 blur-xl" />
                {/* Ellipse 2894: 39.81 x 81.48 at (183.75, 243.75), turned
                    -45deg, #DF9BFF at 35%, blurred 24. */}
                <span className="absolute top-[81.25%] left-[61.25%] h-[27.16%] w-[13.27%] rotate-45 rounded-[50%] bg-[#DF9BFF] opacity-35 blur-xl" />
              </span>

              {/* The frame's glass is deepest at the top-left, away from the
                  #5013FF glow: a soft dark pool there, under the light. */}
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_38%_32%,rgba(14,0,48,0.55)_0%,rgba(14,0,48,0.25)_35%,rgba(14,0,48,0)_65%)]"
              />
              {/* Ellipse 2899: at 30%, an inner shadow of white at 3/3
                  blurred 4 -- the fine light along the top-left inside edge. */}
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full opacity-30 shadow-[inset_3px_3px_4px_#FFFFFF]"
              />

              {/* The rim, as one layer that turns while hovered: the rings,
                  a glint arc, and the spark sitting on the arc at the
                  top-left. Grouped so they turn together and the spark stays
                  on its arc. The rings are masked down to the rim; the spark
                  is not, since its glow spills past it. */}
              <span
                aria-hidden="true"
                className="js-rim pointer-events-none absolute inset-0 rounded-full"
              >
                {/* Ellipse 2887: a 6px ring, #FF9179 to #FF5CF0 to #8466FF
                    to #002FA8 top to bottom with black at 20% under it, the
                    whole layer blurred 16. The blur is on a wrapper rather
                    than the ring so it spreads past the rim both ways, as
                    a layer blur does; on the ring itself the mask would
                    clip it. */}
                <span className="absolute inset-0 rounded-full blur-lg">
                  <span style={RING} className="absolute inset-0 rounded-full bg-black/20 p-1.5" />
                  <span
                    style={RING}
                    className="absolute inset-0 rounded-full bg-[linear-gradient(180deg,#FF9179_0%,#FF5CF0_33%,#8466FF_67%,#002FA8_100%)] p-1.5"
                  />
                </span>
                {/* Ellipse 2886: a 1px ring, #640DFB fading to 10%. */}
                <span
                  style={RING}
                  className="absolute inset-0 rounded-full bg-[linear-gradient(180deg,#640DFB_0%,rgba(100,13,251,0.1)_100%)] p-px"
                />
                {/* The group's own border: 1px, white to nothing, top side. */}
                <span
                  style={RING}
                  className="absolute inset-0 rounded-full bg-[linear-gradient(135deg,#fff_0%,rgba(153,153,153,0)_60%)] p-px"
                />
                {/* The white along the rim: a 1px arc peaking at 305deg --
                    the spark's bearing from the centre -- so the rim is lit
                    around it the way the frame's is, with a glow off it. */}
                <span
                  style={RING}
                  className="absolute inset-0 rounded-full bg-[conic-gradient(from_215deg,transparent_0deg,#fff_90deg,transparent_180deg)] p-px drop-shadow-[0_0_6px_rgba(255,255,255,0.9)]"
                />
                {/* Ellipse 2898: white, 35.33 x 17.06 at (31.8, 70.3), turned
                    40.38deg along the rim, blurred 16 -- the bloom -- with a
                    tight bright point at its heart, as the frame reads. */}
                <span className="absolute top-[23.4%] left-[10.6%] h-[5.7%] w-[11.8%] rotate-[-40.38deg] rounded-[50%] bg-white blur-lg" />
                <span className="absolute top-[16%] left-[4%] size-[10%] rounded-full bg-[radial-gradient(circle,#fff_0%,rgba(255,255,255,0.7)_25%,rgba(255,255,255,0)_70%)] blur-[2px]" />
                <span className="absolute top-[11%] left-[-1%] size-[20%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.7)_0%,rgba(255,255,255,0)_65%)] blur-[6px]" />
              </span>
              {/* Inspector: title Inter Tight 600, 18 on 22.5, tracked 1.4px,
                  caps; body Inter 400, 16 on 24, white at 65%; both 226 wide. */}
              <h3 className="font-tight text-on-night relative z-10 w-56.5 overflow-hidden text-[18px] leading-[22.5px] font-semibold tracking-[1.4px] uppercase">
                <span className="js-orb-title block">{reason.title}</span>
              </h3>
              <p className="js-orb-body relative z-10 mt-3 w-56.5 font-sans text-base leading-6 text-white/65">
                {reason.body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Four plans on one translucent panel; the two in the middle are white and
 * stand proud of it above and below.
 */
function Pricing() {
  return (
    <section id="pricing" className="bg-night relative overflow-hidden py-20 lg:py-28">
      <Glow className="top-0 -left-40" size={640} opacity={0.12} />
      <Glow className="-right-32 bottom-0" size={520} tone="lilac" opacity={0.1} />

      <div className="wonder-container wonder-measure relative">
        <h2 className="font-tight text-on-night text-center text-[clamp(2.25rem,3.125vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.0333em]">
          Simple, transparent <span className="text-iris-300">pricing.</span>
        </h2>
        <p className="text-on-night-muted mt-6 text-center text-[clamp(1rem,1.04vw,1.25rem)]">
          Start free as a student. Startups pay only when they&rsquo;re ready to hire.
        </p>

        <div className="relative mx-auto mt-16 max-w-320 lg:my-24">
          <div
            aria-hidden="true"
            className="absolute inset-0 hidden rounded-[28px] border border-white/10 bg-white/6 lg:block"
          />
          <ul className="relative grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
            {PLANS.map((plan) => (
              <li key={plan.name} className={cn('flex', plan.featured && 'lg:-my-6 lg:px-3')}>
                <PlanCard {...plan} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ name, price, unit, tagline, features, featured }) {
  return (
    <article
      className={cn(
        'flex w-full flex-col rounded-[22px] p-8',
        featured
          ? 'bg-white text-night shadow-[0_24px_60px_rgba(0,0,0,0.35)]'
          : 'text-on-night rounded-none border border-white/10 bg-white/6 lg:border-0 lg:bg-transparent',
      )}
    >
      <p className="flex items-baseline gap-2">
        <span className="font-tight text-4xl font-semibold tracking-tight">{price}</span>
        <span className={cn('text-sm', featured ? 'text-night/60' : 'text-white/60')}>{unit}</span>
      </p>
      <h3 className="font-tight mt-5 text-lg font-semibold">{name}</h3>
      <p className={cn('mt-1 text-sm', featured ? 'text-night/60' : 'text-white/60')}>{tagline}</p>

      <ul className="mt-6 space-y-3">
        {features.map((feature) => (
          <li key={feature} className="flex items-center gap-2.5 text-sm font-medium">
            <span className="bg-iris-500 grid size-4 shrink-0 place-items-center rounded-full text-white">
              <Icon name="check" size={10} />
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <LinkButton
        to={paths.registerCompany}
        variant={featured ? 'light' : 'outlineLight'}
        shape="pill"
        size="pillSm"
        className={cn('mt-8', featured ? 'bg-night text-white hover:bg-night/90' : 'self-start')}
      >
        Post {featured ? 'An ' : ''}Opportunity
        <Icon name="arrowRight" size={14} />
      </LinkButton>
    </article>
  );
}
